import { useCallback, useEffect, useRef, useState } from "react";
import { msalInstance, isMsalConfigured } from "../auth/msalConfig";

// Customer posture — unchanged. Everyone who is not a Patriot internal user
// gets this 30-min limit exactly as before.
export const IDLE_LIMIT_MS = 30 * 60 * 1000;
export const WARN_AT_MS = 28 * 60 * 1000;
// Internal (Patriot) posture. Internal users live in the portal all day, so a
// 30-min idle logout is pure friction for them; the customer posture is
// unchanged at 30 min. A ceiling is still retained for internal because these
// are the highest-privilege sessions — 8 hours caps an abandoned internal
// session rather than leaving it open indefinitely. Warning fires at 7h58m,
// mirroring the customer 2-minutes-before-limit warning window.
export const INTERNAL_IDLE_LIMIT_MS = 8 * 60 * 60 * 1000;
export const INTERNAL_WARN_AT_MS = (8 * 60 - 2) * 60 * 1000; // 7h58m
export const CHECK_MS = 15 * 1000;
export const COUNTDOWN_MS = 1000;
export const THROTTLE_MS = 5 * 1000;

const LAST_ACTIVITY_KEY = "ss_idle_last_activity";
const LOGOUT_KEY = "ss_idle_logout";
const ACTIVITY_EVENTS = ["pointermove", "pointerdown", "keydown", "scroll", "wheel", "touchstart"];

// Role-derived thresholds. Computed per render from the authenticated user's
// isInternal flag (the same signal the rest of the app gates on) and NEVER
// stored in or read from localStorage — localStorage keeps only the activity
// timestamp and the logout broadcast, so the limit cannot be tampered with
// client-side beyond what is already inherent to any frontend timer.
function baseLimits(isInternal) {
  return isInternal
    ? { limitMs: INTERNAL_IDLE_LIMIT_MS, warnAtMs: INTERNAL_WARN_AT_MS }
    : { limitMs: IDLE_LIMIT_MS, warnAtMs: WARN_AT_MS };
}

// Dev-only tester override. CRA removes this branch from production builds,
// including SWA production and PR-preview builds.
function config(isInternal) {
  const base = baseLimits(isInternal);
  let override = null;
  if (
    process.env.NODE_ENV !== "production"
    && typeof window !== "undefined"
    && window.__IDLE_OVERRIDE__
  ) {
    override = window.__IDLE_OVERRIDE__;
  }
  return {
    limitMs: override?.limitMs ?? base.limitMs,
    warnAtMs: override?.warnAtMs ?? base.warnAtMs,
    checkMs: override?.checkMs ?? CHECK_MS,
    countdownMs: COUNTDOWN_MS,
    throttleMs: THROTTLE_MS,
  };
}

export function idlePhase(lastActivity, now, { warnAtMs, limitMs }) {
  const idle = now - lastActivity;
  if (idle >= limitMs) return { phase: "logout", remainingMs: 0 };
  if (idle >= warnAtMs) return { phase: "warning", remainingMs: limitMs - idle };
  return { phase: "active", remainingMs: limitMs - idle };
}

function now() {
  return Date.now();
}

function readLastActivity() {
  const value = Number(localStorage.getItem(LAST_ACTIVITY_KEY));
  return Number.isFinite(value) && value > 0 ? value : now();
}

function writeLastActivity(timestamp) {
  try {
    localStorage.setItem(LAST_ACTIVITY_KEY, String(timestamp));
  } catch (_) {}
}

// The interval and activity gate use the same shared phase so every tab agrees.
// isInternal selects the limit; it never enters localStorage.
function sharedPhase(isInternal) {
  const { warnAtMs, limitMs } = config(isInternal);
  return idlePhase(readLastActivity(), now(), { warnAtMs, limitMs });
}

export default function useIdleTimeout(isInternal = false) {
  const [showWarning, setShowWarning] = useState(false);
  const { limitMs: initialLimitMs, warnAtMs: initialWarnAtMs } = config(isInternal);
  const [remainingMs, setRemainingMs] = useState(initialLimitMs - initialWarnAtMs);
  const lastWriteRef = useRef(0);
  const loggingOutRef = useRef(false);
  const tickRef = useRef(() => {});

  const doLogout = useCallback(() => {
    if (loggingOutRef.current) return;
    loggingOutRef.current = true;
    try {
      localStorage.setItem(LOGOUT_KEY, String(now()));
    } catch (_) {}

    if (isMsalConfigured && msalInstance) {
      msalInstance.logoutRedirect();
    } else {
      window.location.assign(window.location.origin);
    }
  }, []);

  const staySignedIn = useCallback(() => {
    const timestamp = now();
    writeLastActivity(timestamp);
    lastWriteRef.current = timestamp;
    setShowWarning(false);
  }, []);

  const signOutNow = doLogout;

  const tick = useCallback(() => {
    const { phase, remainingMs: nextRemainingMs } = sharedPhase(isInternal);
    if (phase === "logout") {
      doLogout();
      return;
    }
    if (phase === "warning") {
      setShowWarning(true);
      setRemainingMs(nextRemainingMs);
    } else {
      setShowWarning(false);
    }
  }, [doLogout, isInternal]);
  tickRef.current = tick;

  useEffect(() => {
    // A page load immediately after authentication IS activity — the idle
    // clock must never inherit a dead session's timestamp. Seeding
    // only-if-absent let a stale ss_idle_last_activity (>30 min old, from a
    // prior session) survive, so a returning user's first tick computed
    // phase=logout and fired logoutRedirect right after a successful login:
    // an unrecoverable loop until localStorage was cleared by hand. Reset
    // whenever the stored value is absent OR already past the warning
    // threshold; a recent timestamp stays untouched so a genuinely active
    // session opening a new tab keeps cross-tab agreement. This effect must
    // stay declared before the interval effect below — its initial tick()
    // reads the value this seed corrects. The phase is computed with the
    // CURRENT user's limit (isInternal) so an internal user is never reset
    // against the 30-min customer threshold.
    if (!localStorage.getItem(LAST_ACTIVITY_KEY) || sharedPhase(isInternal).phase !== "active") {
      writeLastActivity(now());
    }
  }, [isInternal]);

  useEffect(() => {
    const onActivity = () => {
      const timestamp = now();
      if (timestamp - lastWriteRef.current < config(isInternal).throttleMs) return;
      lastWriteRef.current = timestamp;
      if (sharedPhase(isInternal).phase !== "active") return;
      writeLastActivity(timestamp);
    };
    ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, onActivity, { passive: true });
    });
    const onVisibility = () => {
      if (document.visibilityState === "visible") tickRef.current();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, onActivity);
      });
      document.removeEventListener("visibilitychange", onVisibility);
    };
    // isInternal is a dep because onActivity's phase gate uses the current
    // user's limit; re-subscribing on a role change keeps the gate correct.
  }, [isInternal]);

  useEffect(() => {
    // No staleness guard needed on LOGOUT_KEY: storage events fire only in
    // OTHER tabs and only when the value CHANGES, so a leftover
    // ss_idle_logout value can never re-trigger logout on a future mount —
    // this handler only ever sees a live broadcast from a concurrent tab.
    const onStorage = (event) => {
      if (event.key === LAST_ACTIVITY_KEY) setShowWarning(false);
      if (event.key === LOGOUT_KEY) doLogout();
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [doLogout]);

  useEffect(() => {
    const coarse = setInterval(() => tickRef.current(), config(isInternal).checkMs);
    tick();
    return () => clearInterval(coarse);
  }, [tick]);

  useEffect(() => {
    if (!showWarning) return undefined;
    const fast = setInterval(() => tickRef.current(), config(isInternal).countdownMs);
    return () => clearInterval(fast);
  }, [showWarning]);

  return { showWarning, remainingMs, staySignedIn, signOutNow };
}
